import { AlertTriangle, Droplets, MapPin, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  cleaningUrgency,
  inspectionUrgency,
  ISO_TANKS,
  utilizationTone,
} from './mockAssetData';

function UtilizationBar({ pct }) {
  return (
    <div className="mt-1">
      <div className="flex items-center justify-between text-[9px]">
        <span className="text-logi-text0">Utilization</span>
        <span className="font-mono font-bold text-white">{pct}%</span>
      </div>
      <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-logi-silver/80">
        <div
          className={`h-full rounded-full transition-all ${utilizationTone(pct)}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function IsoTanksGrid({ tanks = ISO_TANKS }) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return tanks.filter(
      (t) =>
        t.id.toLowerCase().includes(q) ||
        t.product.toLowerCase().includes(q) ||
        t.location.toLowerCase().includes(q),
    );
  }, [tanks, query]);

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Droplets className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">ISO Tank Container Registry</h3>
            <p className="text-[11px] text-logi-text0">
              Chemical switching compliance · Inspection tracking · Live utilization
            </p>
          </div>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-logi-text0" />
          <input
            type="search"
            placeholder="Search ID, product, location…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg border border-logi-grid bg-white py-1.5 pl-8 pr-3 text-xs text-logi-text placeholder-logi-subtle outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-logi-accent/30"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map((tank) => {
          const clean = cleaningUrgency(tank.lastCleaning);
          const inspect = inspectionUrgency(tank.inspectionDue);
          const needsAttention = clean.level === 'critical' || inspect.level !== 'ok';

          return (
            <article
              key={tank.id}
              className={`rounded-lg border bg-white p-2.5 backdrop-blur-sm transition-colors hover:bg-logi-silver/80 ${
                needsAttention
                  ? 'border-amber-200 ring-1 ring-amber-500/10'
                  : 'border-logi-grid'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono text-xs font-bold text-logi-accent">{tank.id}</span>
                {needsAttention && (
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-700" strokeWidth={2} />
                )}
              </div>

              <p className="mt-1 truncate text-[11px] font-medium text-logi-text">{tank.product}</p>

              <div className="mt-2 space-y-1.5">
                <div className="flex items-start justify-between gap-1 text-[10px]">
                  <span className="text-logi-text0">Last Cleaning</span>
                  <span className={`text-right font-medium ${clean.className}`}>{clean.label}</span>
                </div>
                <div className="flex items-start justify-between gap-1 text-[10px]">
                  <span className="text-logi-text0">Inspection Due</span>
                  <span
                    className={`rounded px-1.5 py-0.5 text-right font-mono text-[9px] font-semibold ring-1 ${inspect.className}`}
                  >
                    {inspect.label}
                  </span>
                </div>
                <div className="flex items-start gap-1 text-[10px] text-logi-muted">
                  <MapPin className="mt-0.5 h-3 w-3 flex-shrink-0 text-logi-text0" />
                  <span className="line-clamp-2 leading-tight">{tank.location}</span>
                </div>
              </div>

              <UtilizationBar pct={tank.utilization} />
            </article>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <p className="py-8 text-center text-sm text-logi-text0">No ISO tanks match your search.</p>
      )}
    </section>
  );
}
